import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChannelBadge,
  type ChannelKind,
  EmptyState,
  ListPage,
  Progress,
  Skeleton,
  StatusBadge,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@skylabs-digital/backoffice-kit';
import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { metricsApi } from '../api/metrics.api';
import type { TimelineDataPoint } from '../api/metricsTimeline.api';
import { metricsTimelineApi } from '../api/metricsTimeline.api';

type Period = 'today' | 'week' | 'month';
type Granularity = 'hour' | 'day';

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: 'today', label: '24h' },
  { value: 'week', label: '7d' },
  { value: 'month', label: '30d' },
];

const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
  { value: 'hour', label: '1h' },
  { value: 'day', label: '1d' },
];

// Module-scoped Recharts style consts — keep refs stable so SVG doesn't re-render.
const CHART_MARGIN = { top: 8, right: 16, bottom: 8, left: 0 };
const CHART_CURSOR = { fill: 'hsl(var(--muted) / 0.5)' };
const TOOLTIP_CONTENT_STYLE = {
  background: 'hsl(var(--popover))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 'calc(var(--radius) - 2px)',
  fontSize: 12,
  color: 'hsl(var(--popover-foreground))',
};
const TOOLTIP_LABEL_STYLE = { color: 'hsl(var(--foreground))', fontWeight: 600 };
const LEGEND_WRAPPER_STYLE = { fontSize: 12 };

function formatTimestamp(ts: string, granularity: Granularity): string {
  const d = new Date(ts);
  if (granularity === 'hour') {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

interface TimelineChartProps {
  dataPoints: TimelineDataPoint[];
  granularity: Granularity;
  loading: boolean;
}

function TimelineChart({ dataPoints, granularity, loading }: TimelineChartProps) {
  const data = useMemo(
    () =>
      dataPoints.map((p) => ({
        label: formatTimestamp(p.timestamp, granularity),
        sent: p.sent,
        delivered: p.delivered,
        failed: p.failed,
      })),
    [dataPoints, granularity],
  );

  if (loading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (dataPoints.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No timeline data available for this range.
      </p>
    );
  }

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            stroke="hsl(var(--muted-foreground))"
            fontSize={11}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            stroke="hsl(var(--muted-foreground))"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <Tooltip
            cursor={CHART_CURSOR}
            contentStyle={TOOLTIP_CONTENT_STYLE}
            labelStyle={TOOLTIP_LABEL_STYLE}
          />
          <Legend wrapperStyle={LEGEND_WRAPPER_STYLE} iconType="circle" iconSize={8} />
          <Bar dataKey="sent" name="Sent" fill="hsl(var(--info))" radius={[3, 3, 0, 0]} />
          <Bar
            dataKey="delivered"
            name="Delivered"
            fill="hsl(var(--success))"
            radius={[3, 3, 0, 0]}
          />
          <Bar
            dataKey="failed"
            name="Failed"
            fill="hsl(var(--destructive))"
            radius={[3, 3, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function MetricsPage() {
  const [period, setPeriod] = useState<Period>('week');
  const [granularity, setGranularity] = useState<Granularity>('day');

  const timeline = metricsTimelineApi.withQuery({ period, granularity }).useRecord();
  const summary = metricsApi.withQuery({ period }).useRecord();

  const hasError = timeline.error || summary.error;
  if (hasError) {
    return (
      <ListPage title="Metrics" description="Delivery and channel performance over time.">
        <EmptyState
          title="Couldn't load metrics"
          description="Try refreshing in a moment."
          className="border-destructive/40 bg-destructive/5"
        />
      </ListPage>
    );
  }

  const channels = summary.data?.byChannel ?? [];
  const totalSent = channels.reduce((sum, ch) => sum + ch.sent, 0);
  const failuresOnly = channels.filter((m) => m.failed > 0).sort((a, b) => b.failed - a.failed);

  return (
    <ListPage
      title="Metrics"
      description="Delivery and channel performance over time."
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-muted-foreground">Period</span>
          <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
            <TabsList>
              {PERIOD_OPTIONS.map((opt) => (
                <TabsTrigger key={opt.value} value={opt.value}>
                  {opt.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <span className="ml-2 text-xs text-muted-foreground">Granularity</span>
          <Tabs value={granularity} onValueChange={(v) => setGranularity(v as Granularity)}>
            <TabsList>
              {GRANULARITY_OPTIONS.map((opt) => (
                <TabsTrigger key={opt.value} value={opt.value}>
                  {opt.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>Notification timeline</CardTitle>
          <CardDescription>
            Sent, delivered, and failed counts per {granularity === 'hour' ? 'hour' : 'day'}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TimelineChart
            dataPoints={timeline.data?.points ?? []}
            granularity={granularity}
            loading={timeline.loading}
          />
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Volume by channel</CardTitle>
            <CardDescription>Share of total sent across channels.</CardDescription>
          </CardHeader>
          <CardContent>
            {summary.loading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={`s-${i}`} className="h-10 w-full" />
                ))}
              </div>
            ) : channels.length === 0 ? (
              <p className="text-sm text-muted-foreground">No channel data.</p>
            ) : (
              <div className="space-y-3">
                {channels.map((metrics) => {
                  const pct = totalSent > 0 ? Math.round((metrics.sent / totalSent) * 100) : 0;
                  return (
                    <div key={metrics.channel} className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <ChannelBadge channel={metrics.channel as ChannelKind} />
                        <span className="tabular-nums text-muted-foreground">
                          {metrics.sent.toLocaleString()} sent
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Progress value={pct} className="flex-1" />
                        <span className="min-w-[44px] text-right text-xs tabular-nums text-muted-foreground">
                          {pct}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Failures by channel</CardTitle>
            <CardDescription>Channels with non-zero failure counts.</CardDescription>
          </CardHeader>
          <CardContent>
            {summary.loading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={`s-${i}`} className="h-10 w-full" />
                ))}
              </div>
            ) : channels.length === 0 ? (
              <p className="text-sm text-muted-foreground">No channel data.</p>
            ) : failuresOnly.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-success">
                <StatusBadge tone="success">All clear</StatusBadge>
                No failures recorded.
              </div>
            ) : (
              <div className="space-y-3">
                {failuresOnly.map((metrics) => {
                  const rate = metrics.sent > 0 ? (metrics.failed / metrics.sent) * 100 : 0;
                  const tone = rate >= 5 ? 'danger' : rate >= 2 ? 'warning' : 'neutral';
                  return (
                    <div
                      key={metrics.channel}
                      className="flex items-center justify-between gap-3 text-sm"
                    >
                      <ChannelBadge channel={metrics.channel as ChannelKind} />
                      <div className="flex items-center gap-3 text-right">
                        <span className="tabular-nums text-destructive">{metrics.failed}</span>
                        <span className="tabular-nums text-muted-foreground">/ {metrics.sent}</span>
                        <StatusBadge
                          tone={tone}
                          dot={false}
                          className="min-w-[60px] justify-center"
                        >
                          {rate.toFixed(1)}%
                        </StatusBadge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ListPage>
  );
}
